import React, { useState } from 'react';
import type { GoalPeriod, GoalSummary } from '@troia-v3/types';

import { Button } from '@/shared/components/ui';
import { GoalFormDrawer } from '@/modules/goals/components/GoalFormDrawer';

import { DashCard, formatBRL } from '@/shared/components/ui';

interface MetaGaugeCardProps {
  goal: GoalSummary | null;
  initialPeriod?: GoalPeriod;
  initialYear?: number;
  initialMonthOrQuarter?: number;
  /** Há unidade escolhida no filtro: a meta mostrada continua sendo a da EMPRESA. */
  unitFiltered?: boolean;
}

export const MetaGaugeCard: React.FC<MetaGaugeCardProps> = ({
  goal,
  initialPeriod,
  initialYear,
  initialMonthOrQuarter,
  unitFiltered = false,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const subtitle = unitFiltered ? 'Meta da empresa (não filtra por unidade)' : 'Receita ganha vs. objetivo';

  const drawer = (
    <GoalFormDrawer
      isOpen={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      {...(initialPeriod ? { initialPeriod } : {})}
      {...(initialYear !== undefined ? { initialYear } : {})}
      {...(initialMonthOrQuarter !== undefined ? { initialMonthOrQuarter } : {})}
    />
  );

  if (!goal) {
    return (
      <DashCard
        icon="target"
        title="Meta do mês"
        subtitle={subtitle}
        testId="meta-gauge-empty"
        className="h-full"
        bodyClassName="flex flex-col items-center justify-center text-center gap-3"
      >
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhuma meta cadastrada para o período.
        </p>
        <Button
          size="sm"
          variant="primary"
          onClick={() => setDrawerOpen(true)}
          data-testid="meta-gauge-create-goal"
        >
          Cadastrar metas
        </Button>
        {drawer}
      </DashCard>
    );
  }

  const pct = Math.min(100, Math.max(0, goal.achievedPct));
  const angle = (pct / 100) * 180; // 0..180
  const rad = (angle - 180) * (Math.PI / 180);
  const cx = 85;
  const cy = 90;
  const r = 65;
  const endX = cx + r * Math.cos(rad);
  const endY = cy + r * Math.sin(rad);
  const largeArc = angle > 180 ? 1 : 0;
  const arcPath = `M 20 90 A 65 65 0 ${largeArc} 1 ${endX} ${endY}`;

  return (
    <DashCard
      icon="target"
      title="Meta do mês"
      subtitle={subtitle}
      testId="meta-gauge-card"
      className="h-full"
      bodyClassName="flex flex-col justify-center gap-4"
    >
      <div className="relative">
        <svg width="170" height="105" viewBox="0 0 170 105" className="mx-auto block">
          <path
            d="M 20 90 A 65 65 0 0 1 150 90"
            fill="none"
            stroke="var(--theme-card-border)"
            strokeWidth="13"
            strokeLinecap="round"
          />
          <path
            d={arcPath}
            fill="none"
            stroke="var(--theme-primary, #8b5cf6)"
            strokeWidth="13"
            strokeLinecap="round"
          />
        </svg>
        <div
          className="absolute left-1/2 text-2xl font-bold"
          style={{ top: '62%', transform: 'translate(-50%, -50%)', color: 'var(--theme-card-text)' }}
          data-testid="meta-gauge-pct"
        >
          {Math.round(goal.achievedPct)}%
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-xs">
          <span style={{ color: 'var(--theme-muted-text)' }}>Atingido</span>
          <strong style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>
            {formatBRL(goal.achieved)}
          </strong>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span style={{ color: 'var(--theme-muted-text)' }}>Meta do período</span>
          <strong style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>
            {formatBRL(goal.target)}
          </strong>
        </div>
        <div
          className="text-xs rounded-lg px-3 py-2"
          style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: 'var(--theme-card-text)' }}
        >
          Faltam{' '}
          <strong style={{ color: 'var(--theme-primary, #8b5cf6)', fontVariantNumeric: 'tabular-nums' }}>
            {formatBRL(goal.remaining)}
          </strong>{' '}
          em {goal.daysRemaining} {goal.daysRemaining === 1 ? 'dia' : 'dias'}
        </div>
      </div>
      {drawer}
    </DashCard>
  );
};

MetaGaugeCard.displayName = 'MetaGaugeCard';
